import React from "react";

import { cx, css } from "@emotion/css";
import { useFormContext, useWatch } from "react-hook-form";
import { useTranslation } from "react-i18next";

import { useFormError } from "@/hooks/useFormError.ts";
import { useStyles } from "@/hooks/useStyles.ts";
import { FileModel, UploadedFileType } from "@/models/FileModel.ts";
import { Patient } from "@/models/PatientModel.ts";
import { FormInputError } from "@/uiKit/atoms/forms/FormInputError.tsx";
import { type AtomProps, StyleClasses } from "@/uiKit/atoms/utils/AtomTypes.ts";
import { FileManager } from "@/uiKit/molecules/FileManager.tsx";

export type BaseFormFileInputProps = {
  id?: string;
  name: string;
  label: string;
  uploadLabel?: string;
  fileType: UploadedFileType;
  patientInfo: Pick<Patient, "givenName" | "familyName">;
  reserveErrorSpace?: boolean;
  defaultValue?: FileModel;
  disabled?: boolean;
  fullWidth?: boolean;
};

export type FormFileInputProps = AtomProps<BaseFormFileInputProps, StyleClasses<"root" | "input" | "error">>;

export const FormFileInput: React.FC<FormFileInputProps> = ({
  id,
  name,
  label,
  uploadLabel,
  fileType,
  patientInfo,
  reserveErrorSpace = false,
  disabled,
  className,
  classes,
}) => {
  const formContext = useFormContext();
  const { t } = useTranslation();
  const styles = useStyles(makeStyles);
  const formError = useFormError(name);
  const value = useWatch({ name }) as FileModel | undefined;

  return (
    <div id={id} className={cx(styles.root, classes?.root, className)}>
      <FileManager
        idPrefix={id}
        className={cx(styles.fileInput, classes?.input)}
        label={label}
        uploadLabel={uploadLabel}
        defaultFile={value}
        fileType={fileType}
        patientInfo={patientInfo}
        onFilePicked={file => {
          formContext.setValue(name, file, {
            shouldTouch: true,
            shouldValidate: true,
            shouldDirty: true,
          });
        }}
        onDelete={() => {
          formContext.setValue(name, undefined, {
            shouldTouch: true,
            shouldValidate: true,
            shouldDirty: true,
          });
        }}
        readOnly={disabled}
      />
      <FormInputError
        reserveSpace={reserveErrorSpace}
        errorMessage={formError.errorMessage ? t(formError.errorMessage) : undefined}
        className={cx(styles.error, classes?.error)}
      />
    </div>
  );
};

const makeStyles = () => ({
  root: css({
    minWidth: 0,
  }),
  fileInput: css({}),
  error: css({}),
});
